import React, { useState, useRef } from "react";
import { useAtom } from "jotai";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Grid,
    Button,
    Typography,
    TextField,
    IconButton,
    List,
    ListItem,
    Alert,
    CircularProgress,
    Tooltip
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    mbxUserAtom,
    allServicesAtom,
    updateServiceInAllServicesAtom,
    allCategoriesAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    hideCancelButtonAtom,
    warningIconInHeadingAtom
} from "../atoms/atoms";
import { useQuery } from "@tanstack/react-query";
import { execReadByPK } from "../modules/DBService";
import { serviceTypeCategoryByCompany } from "../graphql/queries";
import {
    postApi,
    putApi,
    graphql,
    graphqlOperation
} from "../modules/AmplifyServices";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DialogTheme } from "../styles/DialogTheme";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { formatISO } from "date-fns";
import { Info } from "@mui/icons-material";
import {
    auditCategoryCreate,
    auditCategoryDelete,
    auditCategoryUpdate
} from "../modules/Audit";
import { updateServiceType } from "../graphql/mutations";

export const AddEditCategoriesDialog = ({ open, onClose, categoryClicked }) => {
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [allServices] = useAtom(allServicesAtom);
    const [, updateServiceInAllServices] = useAtom(
        updateServiceInAllServicesAtom
    );

    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setHideCancelButton] = useAtom(hideCancelButtonAtom);
    const [, setWarningIconInHeading] = useAtom(warningIconInHeadingAtom);
    const [categories, setCategories] = useAtom(allCategoriesAtom);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [originalCategories, setOriginalCategories] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [expandedBeforeDrag, setExpandedBeforeDrag] = useState([]);
    const [services, setServices] = useState({});
    const [isModified, setIsModified] = useState(false);
    const [isAddCategoryFocused, setIsAddCategoryFocused] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const dialogContentRef = useRef(null);

    const { isLoading: isLoadingCategories } = useQuery({
        queryKey: ["serviceTypeCategoryByCompany", mbxUser.companyId],
        queryFn: async () => {
            const categoriesResult = await execReadByPK({
                opname: "serviceTypeCategoryByCompany",
                op: serviceTypeCategoryByCompany,
                id: { companyId: mbxUser.companyId },
                filter: { deleted: { ne: true } },
                sortDirection: "DESC"
            });
            return categoriesResult?.items || [];
        },
        onSuccess: async (categories) => {
            const sortedCategories = categories.sort(
                (a, b) => a.ordinal - b.ordinal
            );
            setCategories(
                sortedCategories.map((item) => ({
                    id: item.id,
                    name: item.name,
                    ordinal: item.ordinal
                }))
            );
            setOriginalCategories(
                sortedCategories.map((item) => ({
                    id: item.id,
                    name: item.name,
                    ordinal: item.ordinal
                }))
            );

            // Expand category if categoryClicked is passed
            if (categoryClicked) {
                const categoryClickedId = sortedCategories.find(
                    (item) => item.name === categoryClicked
                )?.id;

                if (categoryClickedId) {
                    await handleExpandCategory(categoryClickedId);
                }
            }
        }
    });

    const getServicesByCategory = async (categoryId) => {
        const services = allServices
            .filter(
                (service) =>
                    service.categoryId === categoryId &&
                    service.isBundledService !== true
            )
            .sort((a, b) => a.ordinal - b.ordinal);

        return services;
    };

    const getChangedCategories = (categories, originalCategories) => {
        return categories.reduce((changed, cat, idx) => {
            // Find matching original
            const orig = originalCategories.find((o) => o.id === cat.id);
            // If no original (new category), skip
            if (!orig) return changed;
            // If name or ordinal changed, include
            if (cat.name !== orig.name || cat.ordinal !== orig.ordinal) {
                changed.push({
                    ...cat,
                    ordinal: idx + 1,
                    desc: cat.desc || cat.name
                });
            }
            return changed;
        }, []);
    };

    const handleAddCategory = () => {
        if (newCategoryName.trim() === "") return;
        const newCategory = { id: null, name: newCategoryName };
        const updatedCategories = [newCategory, ...categories];
        setCategories(updatedCategories);
        setNewCategoryName("");
        setIsModified(
            categoriesAreModified(updatedCategories, originalCategories)
        );
    };

    const categoriesAreModified = (a, b) => {
        if (a.length !== b.length) return true;
        for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id || a[i].name !== b[i].name) return true;
        }
        return false;
    };

    const handleEditCategory = (index, newName) => {
        const updatedCategories = [...categories];
        updatedCategories[index] = {
            ...updatedCategories[index],
            name: newName
        };
        setCategories(updatedCategories);
        setIsModified(
            categoriesAreModified(updatedCategories, originalCategories)
        );
    };

    const handleDeleteCategory = async (index) => {
        const categoryToDelete = categories[index];

        try {
            // Get the services associated with the category
            const services = await getServicesByCategory(categoryToDelete.id);
            if (services.length > 0) {
                // If the category contains services, show the warning dialog
                setConfirmationFunction(() => {
                    setConfirmationDialogOpen(false);
                    setWarningIconInHeading(false);
                    setHideCancelButton(false);
                });
                setConfirmationDialogContent(
                    <Grid container gap={2.25}>
                        <Typography>
                            This category is currently used by {services.length}{" "}
                            service
                            {services.length > 1 ? "s" : ""} – please assign a
                            new category to each of those before deleting the
                            category:
                        </Typography>
                        <List>
                            {services.map((service) => (
                                <ListItem
                                    key={service.id}
                                    sx={{
                                        alignItems: "center",
                                        padding: "0px 0px 0px 12px",
                                        gap: "0.5rem"
                                    }}
                                >
                                    <CircleIcon
                                        sx={{
                                            width: "0.25rem",
                                            height: "0.25rem",
                                            color: theme.palette.text.secondary
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: theme.palette.text.secondary
                                        }}
                                        variant="body2"
                                    >
                                        {service.name}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                );
                setHideCancelButton(true);
                setConformationDialogTitle("Service category in use");
                setWarningIconInHeading(true);
                setConformationDialogSubmitButton("Close");
                setConfirmationDialogOpen(true);
                return;
            }
            // Otherwise proceed with deletion
            const updatedCategories = categories.filter((_, i) => i !== index);
            setCategories(updatedCategories);
            setIsModified(true);
        } catch (error) {
            console.error(
                "Failed to retrieve services or delete category:",
                error
            );
        }
    };

    const reorderCategories = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result.map((category, index) => ({
            ...category,
            ordinal: index + 1
        }));
    };

    const onDragEndCategories = (result) => {
        const { source, destination } = result;

        // If dropped outside the list, do nothing
        if (!destination) return;

        const reorderedCategories = reorderCategories(
            categories,
            source.index,
            destination.index
        );
        setCategories(reorderedCategories);
        setIsModified(true);
    };

    const handleExpandCategory = async (categoryId) => {
        if (expandedCategories.includes(categoryId)) {
            // Collapse the category if it's already expanded
            setExpandedCategories((prev) =>
                prev.filter((id) => id !== categoryId)
            );
        } else {
            // Otherwise, expand the category
            const categoryServices = await getServicesByCategory(categoryId);

            setServices((prev) => ({
                ...prev,
                [categoryId]: categoryServices
            }));

            setExpandedCategories((prev) => [...prev, categoryId]);
        }
    };

    const reorderServices = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result.map((service, index) => ({
            ...service,
            ordinal: index + 1
        }));
    };

    const onDragEndServices = (result, categoryId) => {
        const { source, destination } = result;

        // If dropped outside the list, do nothing
        if (!destination) return;

        const reorderedServices = reorderServices(
            services[categoryId],
            source.index,
            destination.index
        );

        setServices((prev) => ({
            ...prev,
            [categoryId]: reorderedServices
        }));

        setIsModified(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        const now = formatISO(new Date());
        const newCategories = [];
        const existingCategories = [];
        const deletedCategories = [];

        // Separate new, existing, and deleted categories
        // Deleted categories
        const currentCategoryIds = categories.map((category) => category.id);
        for (let i = 0; i < originalCategories.length; i++) {
            const originalCategory = originalCategories[i];
            if (!currentCategoryIds.includes(originalCategory.id)) {
                deletedCategories.push(originalCategory);
            }
        }

        for (let i = 0; i < categories.length; i++) {
            const category = categories[i];
            if (category.id) {
                // Existing categories
                existingCategories.push({
                    id: category.id,
                    ordinal: i + 1,
                    name: category.name,
                    desc: category.desc || category.name
                });
            } else {
                // New categories
                newCategories.push({
                    companyId: mbxUser.companyId,
                    name: category.name,
                    desc: category.name,
                    ordinal: i + 1
                });
            }
        }

        try {
            const deletePromises = deletedCategories.map(async (category) => {
                try {
                    const options = {
                        body: {
                            deleteCategoryInfo: { categoryId: category.id }
                        }
                    };
                    const result = await postApi(
                        "serviceManagement",
                        `/category`,
                        options
                    );

                    if (result) {
                        await auditCategoryDelete(mbxUser, category);
                        console.log("Successfully deleted category:", result);
                    }
                } catch (error) {
                    console.error(
                        `Error deleting category ${category.name}:`,
                        error
                    );
                }
            });
            await Promise.all(deletePromises);

            const changedCategories = getChangedCategories(
                categories,
                originalCategories
            );

            const updatePromises = changedCategories.map(async (category) => {
                try {
                    const data = {
                        name: category.name,
                        desc: category.desc || category.name,
                        ordinal: category.ordinal,
                        updatedAt: now
                    };

                    const result = await putApi(
                        "serviceManagement",
                        `/category/${category.id}`,
                        {
                            body: data
                        }
                    );

                    if (result) {
                        const servicesToUpdate = allServices.filter(
                            (service) => service.categoryId === category.id
                        );

                        servicesToUpdate.forEach((service) => {
                            const updatedServiceData = {
                                ...service,
                                categoryName: category.name,
                                category: {
                                    ...service.category,
                                    name: category.name
                                }
                            };
                            updateServiceInAllServices(updatedServiceData);
                        });

                        await auditCategoryUpdate(
                            mbxUser,
                            category,
                            originalCategories.find((o) => o.id === category.id)
                        );
                    }
                } catch (error) {
                    console.error(
                        `Error updating category ${category.name}:`,
                        error
                    );
                }
            });
            await Promise.all(updatePromises);

            const createPromises = newCategories.map(async (category) => {
                try {
                    const options = {
                        body: {
                            companyId: category.companyId,
                            name: category.name,
                            desc: category.desc || category.name,
                            ordinal: category.ordinal
                        }
                    };

                    const result = await postApi(
                        "serviceManagement",
                        "/category",
                        options
                    );
                    if (result) {
                        await auditCategoryCreate(mbxUser, category);
                        console.log(
                            `Successfully created category: ${category.name}`
                        );
                    }
                } catch (error) {
                    console.error(
                        `Error creating category ${category.name}:`,
                        error
                    );
                }
            });
            await Promise.all(createPromises);

            const updateServicePromises = Object.keys(services).map(
                async (categoryId) => {
                    const serviceList = services[categoryId];
                    try {
                        const servicePromises = serviceList.map(
                            async (service, i) => {
                                const updatedOrdinal = {
                                    id: service.id,
                                    name: service.name,
                                    categoryName: service.categoryName,
                                    ordinal: i + 1
                                };
                                const result = await graphql(
                                    graphqlOperation(updateServiceType, {
                                        input: updatedOrdinal
                                    })
                                );

                                if (result?.data?.updateServiceType) {
                                    let updatedServiceData = {
                                        id: service.id,
                                        ordinal: i + 1
                                    };

                                    // Update the service in all serivces atom
                                    updateServiceInAllServices(
                                        updatedServiceData
                                    );
                                }
                            }
                        );
                        await Promise.all(servicePromises);
                    } catch (error) {
                        console.error(
                            `Error updating services for category ${categoryId}:`,
                            error
                        );
                    }
                }
            );
            await Promise.all(updateServicePromises);

            // If everything succeeds, update state and close the dialog
            setOriginalCategories([...categories]);
            setIsModified(false);
            onClose();
        } catch (error) {
            console.error("Error saving categories and services:", error);
        }
        setIsSaving(false);
    };

    const handleCancel = () => {
        setCategories([...originalCategories]);
        setIsModified(false);
        onClose();
    };

    const renderServicesForCategory = (categoryId) => {
        return (
            <DragDropContext
                onDragEnd={(result) => onDragEndServices(result, categoryId)}
            >
                <Droppable droppableId={`services-${categoryId}`}>
                    {(provided) => (
                        <List
                            sx={{ paddingTop: "8px" }}
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {services[categoryId]?.map((service, i) => (
                                <Draggable
                                    key={service.id}
                                    draggableId={service.id}
                                    index={i}
                                >
                                    {(provided) => {
                                        const transform =
                                            provided.draggableProps.style
                                                .transform;
                                        if (transform) {
                                            // Extract the horizontal (x-axis) movement
                                            const t = transform.split(",")[1];
                                            provided.draggableProps.style.transform = `translate(0px, ${t}`;
                                        }
                                        return (
                                            <ListItem
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                    backgroundColor: "white",
                                                    padding: "8px 28px"
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        flexWrap: "nowrap"
                                                    }}
                                                >
                                                    <IconButton
                                                        disableRipple
                                                        {...provided.dragHandleProps}
                                                        sx={{
                                                            cursor: "all-scroll !important"
                                                        }}
                                                    >
                                                        <DragIndicatorIcon
                                                            sx={{
                                                                color: theme
                                                                    .palette
                                                                    .icon
                                                                    .disabled
                                                            }}
                                                        />
                                                    </IconButton>

                                                    <TextField
                                                        fullWidth
                                                        disabled
                                                        value={service.name}
                                                        sx={{
                                                            "& .MuiInputBase-input.Mui-disabled":
                                                                {
                                                                    WebkitTextFillColor:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .main
                                                                }
                                                        }}
                                                    />
                                                </Grid>
                                            </ListItem>
                                        );
                                    }}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </List>
                    )}
                </Droppable>
            </DragDropContext>
        );
    };

    return (
        <>
            <ThemeProvider theme={DialogTheme(theme)}>
                <Dialog
                    maxWidth="sm"
                    open={open}
                    onClose={onClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        Service categories
                    </DialogTitle>

                    <DialogContent
                        ref={dialogContentRef}
                        sx={{
                            overflowY: "auto"
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid
                                item
                                xs={12}
                                sx={{ paddingTop: "0px !important" }}
                            >
                                <TextField
                                    fullWidth
                                    placeholder="Type to add category"
                                    value={newCategoryName}
                                    onFocus={() =>
                                        setIsAddCategoryFocused(true)
                                    }
                                    onBlur={() =>
                                        setIsAddCategoryFocused(false)
                                    }
                                    onChange={(e) =>
                                        setNewCategoryName(e.target.value)
                                    }
                                    InputLabelProps={{
                                        style: {
                                            top:
                                                newCategoryName ||
                                                isAddCategoryFocused
                                                    ? "2px"
                                                    : "-8px"
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    disabled={!newCategoryName}
                                    textTransform="uppercase"
                                    variant="contained"
                                    color="primary"
                                    startIcon={
                                        <AddIcon sx={{ color: "primary" }} />
                                    }
                                    onClick={handleAddCategory}
                                >
                                    <b> Add Category</b>
                                </Button>
                            </Grid>
                            <Grid item>
                                <Alert
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "6px 16px",
                                        marginBottom: "4px",
                                        border: `1px solid ${theme.palette.info.main}`,
                                        backgroundColor:
                                            theme.palette.info.lightest,
                                        textAlign: "left"
                                    }}
                                    severity="info"
                                    icon={<Info fontSize="inherit" />}
                                >
                                    <Typography
                                        variant="body1"
                                        fontWeight={500}
                                        display={"inline"}
                                        color={theme.palette.info.main}
                                    >
                                        Drag to reorder how categories or
                                        services are shown on the online booking
                                        page
                                    </Typography>
                                </Alert>
                            </Grid>
                            {isLoadingCategories ? (
                                <Box
                                    sx={{
                                        position: "relative",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        minHeight: "300px",
                                        minWidth: "100%"
                                    }}
                                >
                                    <CircularProgress color="primary" />
                                </Box>
                            ) : (
                                // Draggable list of categories
                                <Grid
                                    item
                                    xs={12}
                                    sx={{
                                        display: "flex",
                                        paddingLeft: "0px !important"
                                    }}
                                >
                                    <DragDropContext
                                        onBeforeCapture={() => {
                                            // Save the currently expanded categories before collapsing
                                            setExpandedBeforeDrag([
                                                ...expandedCategories
                                            ]);
                                            setExpandedCategories([]); // Collapse all categories when dragging
                                        }}
                                        onDragEnd={(result) => {
                                            onDragEndCategories(result);

                                            // Restore the previously expanded categories
                                            setExpandedCategories([
                                                ...expandedBeforeDrag
                                            ]);
                                        }}
                                    >
                                        <Droppable
                                            droppableId="categories-list"
                                            direction="vertical"
                                        >
                                            {(provided) => (
                                                <List
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        width: "100%",
                                                        gap: "10px"
                                                    }}
                                                >
                                                    {categories.map(
                                                        (
                                                            { id, name },
                                                            index
                                                        ) => (
                                                            <Draggable
                                                                key={id}
                                                                draggableId={
                                                                    name
                                                                }
                                                                index={index}
                                                                direction="vertical"
                                                            >
                                                                {(provided) => {
                                                                    const transform =
                                                                        provided
                                                                            .draggableProps
                                                                            .style
                                                                            .transform;
                                                                    if (
                                                                        transform
                                                                    ) {
                                                                        // Extract the horizontal (x-axis) movement
                                                                        const t =
                                                                            transform.split(
                                                                                ","
                                                                            )[1];
                                                                        provided.draggableProps.style.transform = `translate(0px, ${t}`;
                                                                    }
                                                                    return (
                                                                        <div
                                                                            ref={
                                                                                provided.innerRef
                                                                            }
                                                                        >
                                                                            <ListItem
                                                                                ref={
                                                                                    provided.innerRef
                                                                                }
                                                                                {...provided.draggableProps}
                                                                                sx={{
                                                                                    backgroundColor:
                                                                                        "white",
                                                                                    padding:
                                                                                        "0px"
                                                                                }}
                                                                            >
                                                                                <Grid
                                                                                    sx={{
                                                                                        display:
                                                                                            "flex",
                                                                                        flexDirection:
                                                                                            "row",
                                                                                        width: "100%"
                                                                                    }}
                                                                                >
                                                                                    <IconButton
                                                                                        {...provided.dragHandleProps}
                                                                                        disableRipple
                                                                                        sx={{
                                                                                            cursor: "all-scroll !important"
                                                                                        }}
                                                                                    >
                                                                                        <DragIndicatorIcon
                                                                                            viewBox="-1 0 24 24"
                                                                                            sx={{
                                                                                                color: theme
                                                                                                    .palette
                                                                                                    .icon
                                                                                                    .disabled
                                                                                            }}
                                                                                        />
                                                                                    </IconButton>

                                                                                    <TextField
                                                                                        fullWidth
                                                                                        autoFocus={
                                                                                            name ===
                                                                                            categoryClicked
                                                                                        }
                                                                                        value={
                                                                                            categories[
                                                                                                index
                                                                                            ]
                                                                                                .name
                                                                                        }
                                                                                        disabled={
                                                                                            isSaving
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            handleEditCategory(
                                                                                                index,
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                        InputProps={{
                                                                                            endAdornment:
                                                                                                (() => {
                                                                                                    return (
                                                                                                        id &&
                                                                                                        allServices.some(
                                                                                                            (
                                                                                                                service
                                                                                                            ) =>
                                                                                                                service.categoryId ===
                                                                                                                id
                                                                                                        ) && (
                                                                                                            <IconButton
                                                                                                                disableRipple
                                                                                                                disabled={
                                                                                                                    isSaving
                                                                                                                }
                                                                                                                sx={{
                                                                                                                    padding:
                                                                                                                        "0px 0px 0px 14px"
                                                                                                                }}
                                                                                                                onClick={() =>
                                                                                                                    handleExpandCategory(
                                                                                                                        id
                                                                                                                    )
                                                                                                                }
                                                                                                            >
                                                                                                                {expandedCategories.includes(
                                                                                                                    id
                                                                                                                ) ? (
                                                                                                                    <Tooltip
                                                                                                                        title="Collapse"
                                                                                                                        placement="top"
                                                                                                                    >
                                                                                                                        <RemoveIcon
                                                                                                                            sx={{
                                                                                                                                color: "primary.main",
                                                                                                                                width: "18px",
                                                                                                                                height: "18px",
                                                                                                                                ...(isSaving && {
                                                                                                                                    color: "disabled.main"
                                                                                                                                })
                                                                                                                            }}
                                                                                                                        />
                                                                                                                    </Tooltip>
                                                                                                                ) : (
                                                                                                                    <Tooltip
                                                                                                                        title="Expand"
                                                                                                                        placement="top"
                                                                                                                    >
                                                                                                                        <AddIcon
                                                                                                                            sx={{
                                                                                                                                color: "primary.main",
                                                                                                                                width: "18px",
                                                                                                                                height: "18px",
                                                                                                                                ...(isSaving && {
                                                                                                                                    color: "disabled.main"
                                                                                                                                })
                                                                                                                            }}
                                                                                                                        />
                                                                                                                    </Tooltip>
                                                                                                                )}
                                                                                                            </IconButton>
                                                                                                        )
                                                                                                    );
                                                                                                })()
                                                                                        }}
                                                                                    />

                                                                                    <IconButton
                                                                                        disableRipple
                                                                                        disabled={
                                                                                            isSaving
                                                                                        }
                                                                                        sx={{
                                                                                            paddingRight:
                                                                                                "0px",
                                                                                            paddingLeft:
                                                                                                "5px"
                                                                                        }}
                                                                                        onClick={() =>
                                                                                            handleDeleteCategory(
                                                                                                index
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <DeleteIcon variant="deleteIcon" />
                                                                                    </IconButton>
                                                                                </Grid>
                                                                            </ListItem>
                                                                            {expandedCategories.includes(
                                                                                id
                                                                            ) &&
                                                                                renderServicesForCategory(
                                                                                    id
                                                                                )}
                                                                        </div>
                                                                    );
                                                                }}
                                                            </Draggable>
                                                        )
                                                    )}
                                                </List>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Grid
                            container
                            item
                            gap="18px"
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end"
                            }}
                        >
                            <Button
                                variant="cancelButton"
                                onClick={handleCancel}
                                disabled={isSaving}
                                sx={{
                                    borderColor: "primary.main"
                                }}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                color="primary"
                                variant="contained"
                                loading={isSaving}
                                disabled={!isModified}
                                onClick={handleSave}
                            >
                                Save
                            </LoadingButton>
                        </Grid>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </>
    );
};
